import type { SupabaseClient } from "@supabase/supabase-js";
import type { Json } from "@/integrations/supabase/types";

/**
 * Write one stage zone. The only place the client states how a stage zone
 * reaches the database.
 *
 * There used to be two: a hand-drawn zone went through the
 * `upsert_stage_zone` RPC, and "Copy from Day 1" did a raw insert. Only the
 * RPC stamps `briefing_stage_id` — the FK that keeps a zone's name alive
 * through a stage rename and that the per-day zone match relies on — so
 * every copied zone was second-class, and one of them (`day1_stage14`) spent
 * a live festival on the map belonging to no stage at all.
 *
 * Making the two agree is not the same as making them one. They are one
 * here, so the next change to this contract cannot be made in one place and
 * forgotten in the other.
 *
 * The RPC is used rather than PostgREST's upsert because the table has
 * several partial unique indexes sharing the same conflict columns, and
 * PostgREST omits the WHERE predicate from ON CONFLICT inference — which
 * tripped 42P10 on staging. The RPC writes the explicit
 * `ON CONFLICT … WHERE area_type='stage_zone'` clause and runs the
 * ownership and polygon validation server-side.
 */
export async function upsertStageZone(
  supabase: SupabaseClient,
  args: {
    briefingId: string;
    /** The wizard's per-day stage slug, e.g. `day1_stage3`. */
    stageSlug: string;
    dayIndex: number;
    /** A GeoJSON geometry (not a Feature — unwrap before calling). */
    polygon: Json;
    /** The producer-visible stage label for THIS day. */
    label: string;
    /** briefing_stages.id for this day, or null to let the server resolve. */
    briefingStageId: string | null;
  },
): Promise<string> {
  const { data, error } = await supabase.rpc("upsert_stage_zone", {
    p_briefing_id: args.briefingId,
    p_stage_id: args.stageSlug,
    p_day_index: args.dayIndex,
    p_polygon: args.polygon,
    p_name: args.label,
    p_briefing_stage_id: args.briefingStageId,
  });

  if (error) throw error;
  const insertedId = (data as { id: string } | null)?.id;
  // A response without an id means the RPC declined to write. Silent on the
  // copy path before this existed, so a zone that never landed was counted
  // as copied.
  if (!insertedId) throw new Error("upsert_stage_zone: no id returned");
  return insertedId;
}
